import React, { useEffect } from 'react'
import { observer } from 'mobx-react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { logger } from 'spartacus/services/LoggerService'
import useStores from 'spartacus/hooks/useStores'
import Alert from 'spartacus/components/Alert'
import Typography from 'spartacus/components/Typography'
import Button from 'spartacus/components/Button'
import { Fields, Step } from 'spartacus/views/OnboardingView'
import PasswordInput, { passwordValidationSchema } from 'spartacus/components/PasswordInput'
import * as S from './style'

interface Props {
  initialValues: Fields[Step.Password]
  loading: boolean
  errorMessage: string
  onChange: (updatedFields: Fields[Step.Password]) => void
  onNext: () => void
  subtitle?: string
}

const PasswordStep = observer(
  ({
    initialValues,
    loading,
    subtitle = 'We recommend using a password manager.',
    errorMessage,
    onChange,
    onNext,
  }: Props): JSX.Element => {
    const { analyticsStore } = useStores()
    const formik = useFormik<Fields[Step.Password]>({
      initialValues,
      validationSchema: yup.object({
        password: passwordValidationSchema,
        passwordConfirmation: yup
          .string()
          .oneOf([yup.ref('password')], "Passwords don't match")
          .required('Please retype your password to confirm it was entered correctly'),
      }),
      onSubmit: () => {
        analyticsStore.event({
          category: 'user auth 2.0 enrollment',
          action: 'password',
          label: 'next',
        })

        onNext()
      },
    })

    useEffect(() => {
      onChange(formik.values)
    }, [formik.values])

    useEffect(() => {
      logger.debug('[user auth 2.0 enrollment] password view')
    }, [])

    return (
      <S.Card>
        <Typography element="h1" kind="TS4 - Strong">
          Create your password
        </Typography>

        <Typography element="p" kind="TS2 - Regular">
          {subtitle}
        </Typography>

        {errorMessage && <Alert>{errorMessage}</Alert>}

        <form onSubmit={formik.handleSubmit}>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            label="Create Password*"
            {...formik.getFieldProps('password')}
            error={Boolean(formik.touched.password && formik.errors.password)}
            requirementsVisible
            disabled={loading}
            margin="normal"
            variant="filled"
            fullWidth
            touched={formik.touched.password}
          />

          <PasswordInput
            id="passwordConfirmation"
            autoComplete="new-password"
            label="Confirm Password*"
            {...formik.getFieldProps('passwordConfirmation')}
            error={Boolean(
              formik.touched.passwordConfirmation && formik.errors.passwordConfirmation,
            )}
            helperText={
              formik.touched.passwordConfirmation && formik.errors.passwordConfirmation
                ? formik.errors.passwordConfirmation
                : ''
            }
            disabled={loading}
            margin="normal"
            variant="filled"
            fullWidth
          />

          <Button
            type="submit"
            width="full"
            disabled={Boolean(
              formik.errors.password ||
                !formik.values.password ||
                formik.errors.passwordConfirmation ||
                !formik.values.passwordConfirmation ||
                loading,
            )}
            loading={loading}
          >
            Next
          </Button>
        </form>

        <Typography element="p" kind="TSL - Legal" style={{ marginTop: '2vh' }}>
          *Required Information
        </Typography>
      </S.Card>
    )
  },
)

export default PasswordStep
